"use client"

import type { ChangeEvent, FC } from "react"
import { useEffect, useRef, useState } from "react"
import { IconButton, TextField } from "@mui/material"
import { useNavigate } from "react-router-dom"
import "./SearchResultsMobile.css"
import { getStagesByName } from "../../Services/api"


interface SearchResultItem {
    project_id: number
    project_name: string
    stage_number?: number
    stage_name?: string
    section_number?: number
    section_name?: string
    subsection_number?: number
    subsection_name?: string
    status: number
}

interface SearchResultsMobileProps {
    results: SearchResultItem[]
    searchTerm: string
    onClose: () => void
    searchValue: string
    onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void
    onSearchSubmit: () => void
}



function highlightText(text: string, query: string): React.ReactNode {
    if (!query) return text;
    const regex = new RegExp(`(${query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
        regex.test(part) ? <strong key={i} className="highlight">{part}</strong> : part
    );
}

const SearchResultsMobile: FC<SearchResultsMobileProps> = ({
    results,
    searchTerm,
    onClose,
    searchValue,
    onSearchChange,
    onSearchSubmit
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [onClose])

    return (
        <div className="mobile-popup-overlay">
            <div className="mobile-search-container" ref={containerRef}>
                <div className="mobile-header">
                    <IconButton
                        onClick={onClose}
                        sx={{
                            width: 50,
                            height: 50,
                            "&:hover": {
                                backgroundColor: "#D0E5FF",
                            },
                        }}
                    >
                        <img 
                            src="/images/close-mobile-icon.png" 
                            alt="close" 
                            style={{ 
                                width: 24, 
                                height: 24 
                            }} 
                        />
                    </IconButton>
                    <div className="mobile-title">
                        תוצאות חיפוש למילה "{searchTerm}" -
                    </div>
                </div>
                <div className="mobile-search-bottom-bar">
                    <TextField
                        variant="standard"
                        value={searchValue}
                        onChange={onSearchChange}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                onSearchSubmit();
                            }
                        }}
                        fullWidth
                        placeholder="חיפוש..."
                        slotProps={{
                            input: {
                                sx: {
                                    color: "#1293FE",
                                    fontFamily:  'sans-serif',
                                    textAlign: "right",
                                    "&::placeholder": {
                                        color: "#1293FE",
                                        opacity: 9,
                                    },
                                },
                                disableUnderline: false,
                                endAdornment: (
                                    <img
                                        src="/images/search-icon-mobile.png"
                                        alt="search"
                                        className="search-input-icon"
                                        style={{ 
                                            cursor: "pointer",
                                            display: "block",
                                            minWidth: "21px",
                                            minHeight: "21px"
                                        }}
                                        onClick={onSearchSubmit}
                                        onError={(e) => {
                                            console.error("Search icon failed to load:", e);
                                            e.currentTarget.style.backgroundColor = "#1293FE";
                                            e.currentTarget.style.borderRadius = "50%";
                                        }}
                                    />
                                ),
                            },
                            root: {
                                sx: {
                                    "& .MuiInput-underline:before": {
                                        borderBottomColor: "#1293FE",
                                    },
                                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                        borderBottomColor: "#1293FE",
                                    },
                                    "& .MuiInput-underline:after": {
                                        borderBottomColor: "#1293FE",
                                    },
                                },
                            },
                        }}
                        InputLabelProps={{
                            shrink: false,
                        }}
                    />
                </div>

                <div className="mobile-results">
                    {results.map((item, index) => (
                        <div
                            key={`${item.project_id}-${index}`}
                            className="mobile-result-card"
                            onClick={() => {
                                onClose()
                                navigate(`/project-about/${item.project_id}`)
                            }}
                        >
                            <div className="mobile-project-name">{item.project_name}</div>
                            <div className="mobile-hierarchy">
                                {item.stage_name && (
                                    <div
                                        className={`mobile-stage ${item.stage_name.toLowerCase().includes(searchValue.toLowerCase()) ? 'bold-line' : ''}`}
                                    >
                                        <img src="/images/arrow-left.png" className="arrow" />
                                        {item.stage_name}
                                    </div>
                                )}

                                {item.section_name && (
                                    <div
                                        className={`mobile-section ${item.section_name.toLowerCase().includes(searchValue.toLowerCase()) ? 'bold-line' : ''}`}
                                    >
                                        <img src="/images/arrow-left.png" className="arrow" />
                                        {item.section_name}
                                    </div>
                                )}

                                {item.subsection_name && (
                                    <div
                                        className={`mobile-subsection ${item.subsection_name.toLowerCase().includes(searchValue.toLowerCase()) ? 'bold-line' : ''}`}
                                    >
                                        <img src="/images/arrow-left.png" className="arrow" />
                                        {item.subsection_name}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default SearchResultsMobile
